// features/premium/PlanCard.tsx — one premium tier card.
import styled from 'styled-components';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import type { PremiumTier, PremiumTierId } from '@/core/models/premium';
import type { BillingPeriod } from './BillingToggle';

export interface PlanCardProps {
  tier: PremiumTier;
  period: BillingPeriod;
  onUpgrade: (id: PremiumTierId) => void;
  /**
   * Fix-round F1: true once Checkout has answered "billing_not_configured"
   * (STRIPE_* env unset server-side). There is no payment URL to send anyone
   * to — the legacy $5.00 Payment Link is NOT a fallback (wrong price,
   * unattributable) — so the CTA renders as the disabled "Supporter link
   * coming soon" affordance instead of a live upgrade button.
   */
  billingUnavailable?: boolean;
}

const Card = styled.article<{ $popular: boolean }>`
  position: relative; display: flex; flex-direction: column; gap: 12px;
  padding: 20px; border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme, $popular }) =>
    $popular
      ? `color-mix(in srgb, ${theme.color.accentCyan} 14%, ${theme.color.glassBg})`
      : theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  /* The recommended plan is marked by its border, not by emitting light. */
  box-shadow: none;
`;
const Badge = styled.span`
  position: absolute; top: -10px; left: 20px;
  padding: 3px 10px; border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs}; font-weight: 700;
  color: ${({ theme }) => theme.color.onAccent}; background: ${({ theme }) => theme.color.accentCyan};
`;
const Name = styled.h3`
  margin: 0; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Tagline = styled.div`font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const Price = styled.div`
  font-family: ${({ theme }) => theme.font.display}; font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.xl}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Per = styled.span`font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const Audience = styled.div`font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};`;
const Benefits = styled.ul`list-style: none; margin: 4px 0; padding: 0; display: flex; flex-direction: column; gap: 7px;`;
const BenefitItem = styled.li`
  display: flex; align-items: flex-start; gap: 8px;
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  & svg { flex: none; margin-top: 2px; color: ${({ theme }) => theme.color.accentCyan}; }
`;

// Only Pro is a real, purchasable entitlement today — Advanced/Ultra are
// honest "coming soon": no price presented as available, no checkout path.
const PURCHASABLE_TIER_IDS: ReadonlySet<PremiumTierId> = new Set(['pro']);

// Fix-round F12: and only MONTHLY is purchasable. settings.STRIPE_PRICE_PRO
// is a monthly price — an "annual" upgrade would have quoted $49.99/yr and
// then enrolled the payer in $4.99/mo billing. Annual is presented as coming
// soon and reaches no checkout path at all until a real annual price exists.
const PURCHASABLE_PERIOD: BillingPeriod = 'monthly';

const ComingSoonNote = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};
`;

/** Why this card can't be bought right now — null when it can. */
function unavailableReason(
  tier: PremiumTier,
  period: BillingPeriod,
  billingUnavailable: boolean,
): string | null {
  if (!PURCHASABLE_TIER_IDS.has(tier.id)) return `${tier.name} isn't purchasable yet.`;
  if (period !== PURCHASABLE_PERIOD) {
    return `Annual billing isn't available yet. ${tier.name} is ${tier.monthly} a month.`;
  }
  // Restores the pre-1.23.00 SupportPage affordance verbatim: one plain,
  // always-visible note (never an aria-describedby tooltip — pairing the two
  // made a screen reader announce the same sentence twice for one control).
  if (billingUnavailable) return 'Supporter link coming soon.';
  return null;
}

export function PlanCard({ tier, period, onUpgrade, billingUnavailable = false }: PlanCardProps) {
  // A real, chargeable price exists for this tier+period pair (so "/mo" is
  // meaningful) even when billing happens to be unconfigured right now — the
  // two are separate questions, and only the second is temporary.
  const hasRealPrice = PURCHASABLE_TIER_IDS.has(tier.id) && period === PURCHASABLE_PERIOD;
  // Fix-round F12: the card itself refuses to print a figure checkout cannot
  // charge, rather than trusting the seed to say "Coming soon" — the annual
  // "$49.99/yr" that enrolled people in monthly billing came from exactly
  // that kind of trust.
  const price = hasRealPrice ? tier.monthly : 'Coming soon';
  const per = period === 'monthly' ? '/mo' : '/yr';
  const blockedReason = unavailableReason(tier, period, billingUnavailable);
  const purchasable = blockedReason === null;
  return (
    <Card $popular={!!tier.popular}>
      {tier.popular && <Badge>Most Popular</Badge>}
      <Name>{tier.name}</Name>
      <Tagline>{tier.tagline}</Tagline>
      <Price>{price}{hasRealPrice && <Per> {per}</Per>}</Price>
      <Audience>{tier.audience}</Audience>
      <Benefits>
        {tier.benefits.map((b) => (
          <BenefitItem key={b}><Icon name="check" size={16} />{b}</BenefitItem>
        ))}
      </Benefits>
      {purchasable ? (
        <Button variant={tier.popular ? 'primary' : 'secondary'} onClick={() => onUpgrade(tier.id)}>
          Upgrade to {tier.name}
        </Button>
      ) : (
        <>
          <Button
            variant="secondary"
            aria-disabled="true"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            Coming soon
          </Button>
          <ComingSoonNote>{blockedReason}</ComingSoonNote>
        </>
      )}
    </Card>
  );
}
